import LFCAutocomplete, {multiSelectData} from '_components/inputs/LFCAutocomplete';
import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import ProgressBar from '_components/ProgressBar';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {Box, Dialog, DialogContent, Slider, Stack, Switch, Typography} from '@mui/material';

const SQL_ID_FLOOR_MAP = 8012;

const ICON_COLOR = 'rgb(255, 193, 7)';
const IMAGE_WIDTH_DEFAULT = 480;

interface FloorDevice {
  device: string;
  relative_x: number;
  relative_y: number;
}

interface FloorImage {
  image_no: number;
  image_path: string;
  aspect_ratio: number;
  icon_ratio: number;
  devices: FloorDevice[];
}

interface FloorData {
  floor_id: number;
  floor_name: string;
  images: FloorImage[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectDevice: (deviceName: string) => void;
}

const FloorMapDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [floors, setFloors] = useState<FloorData[]>([]);
  const [floorFilter, setFloorFilter] = useState('');
  const [floorOptions, setFloorOptions] = useState<{label: string}[]>([]);
  const [imageWidth, setImageWidth] = useState(IMAGE_WIDTH_DEFAULT);
  const [showLabels, setShowLabels] = useState(true);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  useEffect(() => {
    if (!props.open) {
      setFloors([]);
      setFloorFilter('');
      return;
    }

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: SQL_ID_FLOOR_MAP,
        parameters: {},
        cancelToken: cancelSource.current.token,
        t
      }).then((datas: any[]) => {
        setFloors(datas);
        const options = datas.map(f => ({label: f.floor_name}));
        setFloorOptions(options);
      })
    ]).then(() => setStartProcess(false));
  }, [props.open]);

  useEffect(() => {
    return () => {
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  const filteredFloors = floorFilter ? floors.filter(f => f.floor_name === floorFilter) : floors;

  const handleDeviceClick = (deviceName: string) => {
    props.onSelectDevice(deviceName);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} fullScreen>
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={props.onClose}>{t('フロアマップ')}</LFCDialogTitle>
      <DialogContent dividers>
        {/* フロア絞り込みフィルター + 端末名トグル + ズームスライダー */}
        <Box mb={2} display="flex" alignItems="center" gap={3}>
          <Box minWidth={320}>
            <LFCAutocomplete
              id="floor-filter"
              name="floor-filter"
              label={t('フロア')}
              size="small"
              value={floorFilter ? multiSelectData(floorOptions, floorFilter) : null}
              onChange={(relayDatas: any) => {
                setFloorFilter(relayDatas === '' ? '' : relayDatas.data);
              }}
              onReset={false}
              doneReset={false}
              multiple={false}
              selectItem={floorOptions}
            />
          </Box>
          <Box flexGrow={1} />
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Switch
              size="small"
              checked={showLabels}
              onChange={(_, checked) => setShowLabels(checked)}
            />
            <Typography variant="body2">{t('端末名')}</Typography>
          </Stack>
          <Box width={200}>
            <Stack spacing={1} direction="row" alignItems="center">
              <ZoomOutIcon />
              <Slider
                size="small"
                value={imageWidth}
                step={50}
                min={200}
                max={1600}
                valueLabelDisplay="off"
                onChange={(_, v) => setImageWidth(v as number)}
              />
              <ZoomInIcon />
            </Stack>
          </Box>
        </Box>

        {/* フロアセクション */}
        {filteredFloors.length === 0 && !startProcess && (
          <Typography color="text.secondary">{t('フロアが登録されていません')}</Typography>
        )}

        {filteredFloors.map(floor => (
          <Box key={floor.floor_id} mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={1}>
              {floor.floor_name}
            </Typography>

            <Box display="flex" gap={2} overflow="auto" pb={1}>
              {floor.images.map(img => {
                const imgHeight = Math.round(imageWidth * img.aspect_ratio);
                const iconSize = Math.round(imageWidth * 0.06 * img.icon_ratio);

                return (
                  <Box key={img.image_no} flexShrink={0}>
                    <Box
                      position="relative"
                      width={imageWidth}
                      height={imgHeight}
                      border="2px solid #888"
                      borderRadius="6px"
                      overflow="hidden"
                      sx={{userSelect: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.5)'}}
                    >
                      <img
                        src={img.image_path}
                        alt={t('{{floorName}} 画像{{imageNo}}', {
                          floorName: floor.floor_name,
                          imageNo: img.image_no
                        })}
                        style={{width: '100%', height: '100%', objectFit: 'fill', display: 'block'}}
                      />

                      {img.devices.map(dev => {
                        const x = (dev.relative_x / 100) * imageWidth - iconSize / 2;
                        const y = (dev.relative_y / 100) * imgHeight - iconSize / 2;

                        return (
                          <Box
                            key={dev.device}
                            position="absolute"
                            sx={{
                              left: x,
                              top: y,
                              cursor: 'pointer',
                              '&:hover .device-label': {opacity: 1}
                            }}
                            onClick={() => handleDeviceClick(dev.device)}
                          >
                            <Box
                              sx={{
                                width: iconSize,
                                height: iconSize,
                                backgroundColor: ICON_COLOR,
                                border: 'solid 2px rgb(96, 125, 139)',
                                borderRadius: '2px',
                                animation: 'floor-map-ripple 1.8s ease-in-out infinite',
                                '@keyframes floor-map-ripple': {
                                  '0%': {boxShadow: '0 0 0 0px rgba(255,193,7,0.7)', opacity: 1},
                                  '50%': {opacity: 0.4},
                                  '80%': {boxShadow: '0 0 0 14px rgba(255,193,7,0)', opacity: 1},
                                  '100%': {boxShadow: '0 0 0 14px rgba(255,193,7,0)', opacity: 1}
                                }
                              }}
                            />
                            <Box
                              className="device-label"
                              sx={{
                                position: 'absolute',
                                top: '-2px',
                                left: '110%',
                                fontSize: `${iconSize / 25}em`,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                padding: '1px 3px',
                                whiteSpace: 'nowrap',
                                opacity: showLabels ? 1 : 0,
                                transition: 'opacity 0.15s',
                                pointerEvents: 'none',
                                zIndex: 2
                              }}
                            >
                              {dev.device}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default FloorMapDialog;
